import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, formatApiErrorDetail } from "@/api";
import { GstVerify } from "@/components/GstVerify";
import { toast } from "sonner";

const L = ({ children }) => (
  <Label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">{children}</Label>
);

const FIELDS = [
  ["contact_person", "Contact person", "e.g. Ramesh Agarwal"],
  ["phone", "Phone", "10 digit mobile"],
  ["whatsapp", "WhatsApp number", "10 digit, used for sharing"],
  ["alt_phone", "Alternate phone", "Office / landline"],
  ["email", "Email", "name@company.com"],
  ["legal_name", "Legal name (GST)", "As per GST records"],
  ["trade_name", "Trade name (GST)", "As per GST records"],
  ["city", "City", "e.g. Bhilwara"],
  ["state", "State", "e.g. Rajasthan"],
  ["pincode", "Pincode", "e.g. 311001"],
  ["transport", "Transport", "Regular transporter"],
];

const empty = FIELDS.reduce((a, [k]) => ({ ...a, [k]: "" }), {
  address: "", notes: "", gstin: "", gst_status: "", gst_verified_at: "",
});

export const SupplierDetailsDialog = ({ supplier, open, onOpenChange, onSaved }) => {
  const [form, setForm] = useState(empty);
  const [stats, setStats] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !supplier) return;
    setLoading(true);
    api
      .get(`/suppliers/${encodeURIComponent(supplier.name)}/profile`)
      .then(({ data }) => {
        setForm({ ...empty, ...Object.fromEntries(Object.entries(data).filter(([k]) => k in empty)) });
        setStats(data.stats);
      })
      .catch((err) => toast.error(formatApiErrorDetail(err.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [open, supplier]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/suppliers/${encodeURIComponent(supplier.name)}/profile`, form);
      toast.success("Supplier details saved");
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="supplier-details-dialog"
        className="left-0 top-0 h-screen max-h-screen w-screen max-w-none translate-x-0 translate-y-0 overflow-y-auto rounded-none border-0 p-0 sm:max-w-none sm:rounded-none lg:w-[calc(100vw-var(--sb-w))]"
      >
        <div className="mx-auto w-full max-w-3xl px-4 py-8 sm:px-8">
          <DialogHeader>
            <DialogTitle className="font-head text-3xl font-extrabold tracking-tighter">Supplier details</DialogTitle>
            <DialogDescription className="text-sm text-zinc-500">{supplier?.name}</DialogDescription>
          </DialogHeader>

          {loading ? (
            <p className="py-12 text-center text-sm text-zinc-500">Loading…</p>
          ) : (
            <form onSubmit={submit} className="mt-6 space-y-4">
              {stats && (
                <div className="grid grid-cols-3 gap-3" data-testid="supplier-stats">
                  {[
                    ["Purchase bills", stats.bills],
                    ["Purchase value ₹", stats.purchase_total],
                    ["Last bill", stats.last_bill_date || "—"],
                  ].map(([label, v]) => (
                    <div key={label} className="border border-zinc-200 bg-zinc-50 px-3 py-2">
                      <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">{label}</p>
                      <p className="mono text-sm font-bold">{v}</p>
                    </div>
                  ))}
                </div>
              )}

              <GstVerify
                testId="supplier"
                value={form.gstin}
                onChange={(v) => setForm((f) => ({ ...f, gstin: v }))}
                onVerified={(d) =>
                  setForm((f) => ({
                    ...f,
                    gstin: d.gstin,
                    legal_name: d.legal_name || f.legal_name,
                    trade_name: d.trade_name || f.trade_name,
                    address: d.address || f.address,
                    city: d.city || f.city,
                    state: d.state || f.state,
                    pincode: d.pincode || f.pincode,
                    gst_status: d.status || "",
                    gst_verified_at: new Date().toISOString(),
                  }))
                }
              />

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {FIELDS.map(([key, label, placeholder]) => (
                  <div key={key}>
                    <L>{label}</L>
                    <Input
                      data-testid={`supplier-${key.replace(/_/g, "-")}-input`}
                      className="mt-2 rounded-sm"
                      value={form[key] ?? ""}
                      onChange={set(key)}
                      placeholder={placeholder}
                    />
                  </div>
                ))}
              </div>

              <div>
                <L>Address</L>
                <Textarea
                  data-testid="supplier-address-input"
                  rows={3}
                  className="mt-2 rounded-sm"
                  value={form.address ?? ""}
                  onChange={set("address")}
                  placeholder="Mill / office address"
                />
              </div>

              <div>
                <L>Notes</L>
                <Textarea
                  data-testid="supplier-notes-input"
                  rows={3}
                  className="mt-2 rounded-sm"
                  value={form.notes ?? ""}
                  onChange={set("notes")}
                  placeholder="Payment terms, agent name, anything useful"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" className="rounded-sm"
                  data-testid="supplier-details-cancel" onClick={() => onOpenChange(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={saving} data-f2-save data-testid="supplier-details-save"
                  className="rounded-sm bg-[var(--mod-primary)] text-white hover:opacity-90">
                  {saving ? "Saving…" : "Save details"}
                </Button>
              </div>
            </form>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
